import React, { FC, createContext, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import CreateEstimateHeader from './components/CreateEstimateHeader';
import CreateEstimateSidebar from './components/CreateEstimateSidebar';
import EstimateCustomer from './components/EstimateCustomer';
import CreateEstimateDetails from './components/CreateEstimateDetails';
import EstimateNotesAndAttachmentsSection from './components/EstimateNotesAndAttachmentsSection';
import EstimateLineItemSection from './components/EstimateLineItemSection';
import { CreateEditEstimateNavEnum } from '../constants';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { Loader } from 'components';
import { CreateEstimateContextType } from '../types';
import {
  selectIsInitialisingEstimateFromAPI,
  updateBillingAddress,
  updateEstimateCustomer,
  updateIsSignatureEnabled,
  updateServiceAddress
} from '../estimatesSlice';
import { useGetMerchantCustomizationsQuery } from 'containers/settings/api';
import { FeatureToggleState } from 'containers/settings/constants';
import { useLazyGetEstimateByIdQuery } from '../api';
import { updateCustomizations } from 'containers/settings/invoiceSettingsSlice';
import { useLazyGetJobByIdQuery } from 'containers/jobs/api';
import {
  useGetTaxesAndFeesQuery,
  useLazyGetCustomerByIdQuery,
  useLazyGetInvoiceByIdQuery
} from 'containers/invoices/api';
import { CustomerAddress } from 'containers/customers/customers.model';
import CreateEstimateMoreOptions from './components/CreateEstimateMoreOptions';

export const CreateEstimateContext = createContext<CreateEstimateContextType>({
  selectedEstimateTab: CreateEditEstimateNavEnum.CUSTOMER,
  setSelectedEstimateTab: null
});

interface CreateEstimateProps {
  onPreview: () => void;
  onCreateAndShare: () => void;
  isSaveInProgress: boolean;
}

const CreateEstimate: FC<CreateEstimateProps> = ({ onPreview, onCreateAndShare, isSaveInProgress }) => {
  const { estimateId } = useParams<{ estimateId: string }>();
  const location = useLocation();
  const isEdit = location.pathname.includes('/edit');
  const isDuplicate = location.pathname.includes('/duplicate');

  const queryParameters = new URLSearchParams(location.search);
  const invoiceId = queryParameters.get('invoiceId');
  const jobId = queryParameters.get('jobId');
  const customerId = queryParameters.get('customerId');

  const [selectedEstimateTab, setSelectedEstimateTab] = useState<CreateEditEstimateNavEnum>(
    CreateEditEstimateNavEnum.CUSTOMER
  );
  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isInitialisingEstimateFromAPI = useAppSelector(selectIsInitialisingEstimateFromAPI);
  const {
    data: merchantCustomisations,
    isSuccess: merchantCustomisationsSuccess,
    isFetching: isFetchingMerchantCustomisations
  } = useGetMerchantCustomizationsQuery({
    teamId: currentTeam.id.toString()
  });

  const { isLoading: isLoadingTaxesAndFees } = useGetTaxesAndFeesQuery({
    teamId: currentTeam?.id
  });

  const [getEstimateById] = useLazyGetEstimateByIdQuery();
  const [getJobById] = useLazyGetJobByIdQuery();
  const [getInvoiceById] = useLazyGetInvoiceByIdQuery();
  const [getCustomerByIdQuery, { isFetching: isFetchingCustomer }] = useLazyGetCustomerByIdQuery();

  useEffect(() => {
    if (merchantCustomisationsSuccess) {
      dispatch(updateCustomizations(merchantCustomisations));
      dispatch(
        updateIsSignatureEnabled(
          merchantCustomisations.customerSignatureConfig === FeatureToggleState.ALWAYS_ON ||
            merchantCustomisations.customerSignatureConfig === FeatureToggleState.ON_BY_DEFAULT
        )
      );
    }
  }, [merchantCustomisationsSuccess, dispatch]);

  const getCustomerById = async () => {
    const response = await getCustomerByIdQuery({ teamId: currentTeam.id, customerId });
    dispatch(
      updateEstimateCustomer({
        ...response.data,
        ID: response.data.id,
        billingAddress: response.data.billingAddress as CustomerAddress,
        serviceAddress: response.data.serviceAddress as CustomerAddress[]
      })
    );
    dispatch(updateServiceAddress(response.data?.serviceAddress[0] as CustomerAddress));
    dispatch(updateBillingAddress(response.data?.billingAddress as CustomerAddress));
  };

  useEffect(() => {
    if (customerId) {
      getCustomerById();
    }
  }, [customerId]);

  useEffect(() => {
    if ((isEdit || isDuplicate) && estimateId) {
      getEstimateById({ teamId: currentTeam.id, estimateId, isFromEdit: true });
    }
  }, [isEdit, estimateId, currentTeam.id]);

  useEffect(() => {
    if (jobId) {
      getJobById({ jobId, teamId: currentTeam.id, isFromEstimate: true });
    }
  }, [jobId, currentTeam.id]);

  useEffect(() => {
    if (invoiceId) {
      getInvoiceById({ invoiceId, teamId: currentTeam.id, isFromEstimate: true });
    }
  }, [invoiceId, currentTeam.id]);

  useEffect(() => {
    const tabElem = document.getElementById(selectedEstimateTab);
    if (tabElem) {
      tabElem.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
    }
  }, [selectedEstimateTab]);

  const handleTabChange = (tab: CreateEditEstimateNavEnum) => {
    setSelectedEstimateTab(tab);
  };

  return (
    <>
      <CreateEstimateContext.Provider value={{ selectedEstimateTab, setSelectedEstimateTab }}>
        {isInitialisingEstimateFromAPI || isLoadingTaxesAndFees || isFetchingCustomer ? (
          <Loader />
        ) : (
          <div className="relative flex h-full w-full flex-col">
            <CreateEstimateHeader
              isEdit={isEdit}
              onSave={onCreateAndShare}
              isActionInProgress={isSaveInProgress}
              onPreview={onPreview}
            />
            <div className="flex h-full w-full flex-row gap-x-[120px] overflow-hidden bg-primary">
              <div className="hidden w-1/3 max-w-[31.25rem] flex-col items-center p-11 pr-0 lg:flex lg:w-80">
                <CreateEstimateSidebar activeTab={selectedEstimateTab} onTabChange={handleTabChange} />
              </div>
              <div
                id="scrollable-estimate-div"
                className="customNormalScroll flex grow flex-col space-y-8 overflow-y-auto p-12 lg:pl-0 lg:pt-9">
                <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditEstimateNavEnum.CUSTOMER}>
                  <EstimateCustomer />
                </div>
                <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditEstimateNavEnum.ESTIMATE_DETAILS}>
                  <CreateEstimateDetails context={CreateEstimateContext} />
                </div>
                <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditEstimateNavEnum.LINE_ITEMS}>
                  <EstimateLineItemSection isLoadingTaxesAndFees={false} context={CreateEstimateContext} />
                </div>
                <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditEstimateNavEnum.NOTES_ATTACHMENTS}>
                  <EstimateNotesAndAttachmentsSection
                    context={CreateEstimateContext}
                    termsAndConditions={merchantCustomisationsSuccess ? merchantCustomisations.estimateTnc : null}
                    isLoadingTermsAndConditions={isFetchingMerchantCustomisations}
                  />
                </div>
                <div className="lg:w-4xl flex max-w-4xl flex-col gap-y-8">
                  <CreateEstimateMoreOptions />
                </div>
              </div>
            </div>
          </div>
        )}
      </CreateEstimateContext.Provider>
    </>
  );
};

export default CreateEstimate;
